/*
 * Stay Fresh: native add-on options, cart handling and shipping-time notice.
 * Replaces the Globo Product Options and Warnify Pro Warnings apps.
 *
 * Product page (snippets/sf-addons.liquid inside the product form):
 *   - choices update the shown price and "Selections will add $X to the price";
 *   - Add to cart puts the main product in the cart, then each paid add-on as a
 *     line nested under it (Shopify nested cart lines, parent_line_key), with
 *     the same quantity, and goes to the cart page (as Globo did).
 * Cart page and cart drawer:
 *   - add-on lines have no quantity box or Remove link; they follow the
 *     product they belong to (quantity kept equal, removed with it);
 *   - add-on lines without their product (left over from Globo, or added
 *     directly) are taken out of the cart;
 *   - Check out first makes sure every add-on matches its freeze dryer.
 * Shipping-time notice (sections/sf-shipping-notice.liquid, off by default):
 *   - when on, Add to cart first asks the buyer to confirm the product's
 *     "Ships in" weeks (product metafield custom.ship_time).
 *
 * The theme (theme.min.js) changes cart quantities by LINE NUMBER, after a
 * 500 ms pause in typing or a Remove animation. The cart code below therefore
 * never takes lines out while the theme may be about to act on a line number,
 * and holds Remove clicks and typing while it does take lines out.
 */
(function () {
  'use strict';

  if (window.sfAddons) return;
  var api = (window.sfAddons = { version: 4 });

  var ROOT = '[data-sf-addons]';
  var INPUT = '[data-sf-addon-input]';
  var QTY = '[data-cart-item-input-quantity]';
  var CARTS = '[data-section-type="cart-template"], [data-section-type="cart-drawer"]';
  var supportsFetch = typeof window.fetch === 'function' && typeof window.Promise === 'function';

  /* ------------------------------------------------------------------ utils */

  function matches(el, sel) {
    var fn = el && el.nodeType === 1 && (el.matches || el.msMatchesSelector || el.webkitMatchesSelector);
    return !!fn && fn.call(el, sel);
  }

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (matches(el, sel)) return el;
      el = el.parentNode;
    }
    return null;
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function cartUrl() {
    return root() + 'cart';
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function stop(e) {
    e.preventDefault();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    else e.stopPropagation();
  }

  function newEvent(type) {
    try {
      return new Event(type, { bubbles: true, cancelable: true });
    } catch (e) {
      var ev = document.createEvent('Event');
      ev.initEvent(type, true, true);
      return ev;
    }
  }

  // "123456" cents -> "1,234.56", then dropped into the store's own format,
  // taken from a sample the theme rendered ({{ 123456 | money }}).
  function formatMoney(cents, sample) {
    cents = Math.round(Number(cents) || 0);
    var neg = cents < 0;
    var s = (Math.abs(cents) / 100).toFixed(2).split('.');
    var amount = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + s[1];
    var out = sample && sample.indexOf('1,234.56') !== -1 ? sample.replace('1,234.56', amount) : '$' + amount;
    return neg ? '-' + out : out;
  }

  // JSON request to the Ajax cart API. Gives up after REQUEST_TIMEOUT ms (a
  // stalled connection), so nothing waits on it forever.
  var REQUEST_TIMEOUT = 8000;

  function request(path, body) {
    var opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    var ctrl = typeof window.AbortController === 'function' ? new AbortController() : null;
    if (ctrl) opts.signal = ctrl.signal;
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        var err = new Error(cartErrorText());
        err.status = 0;
        err.timeout = true;
        reject(err);
        if (ctrl) {
          try { ctrl.abort(); } catch (e) { /* already done */ }
        }
      }, REQUEST_TIMEOUT);
      fetch(root() + path, opts).then(function (res) {
        return res.text().then(function (text) {
          var data = null;
          try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
          if (!res.ok) {
            var err = new Error((data && (data.description || data.message)) || 'Request failed');
            err.status = res.status;
            err.data = data;
            throw err;
          }
          return data;
        });
      }).then(function (data) {
        clearTimeout(timer);
        resolve(data);
      }, function (err) {
        clearTimeout(timer);
        reject(err);
      });
    });
  }

  function getCart() {
    return request('cart.js');
  }

  function props(item) {
    return (item && item.properties) || {};
  }

  function cartErrorText() {
    return (window.theme && theme.strings && theme.strings.cartError) || 'Something went wrong. Please try again.';
  }

  /* -------------------------------------------------------- product: choices */

  // All data-* names carry "sf-": the theme rewrites every [data-compare-price]
  // (and similar) element in the product section when the variant changes.

  function selected(addons) {
    var out = [];
    var seen = {};
    each(addons.querySelectorAll(INPUT), function (input) {
      if (input.checked && !input.disabled && input.value && !seen[input.value]) {
        seen[input.value] = true;
        out.push({ id: Number(input.value), price: Number(input.getAttribute('data-sf-price')) || 0 });
      }
    });
    return out;
  }

  function scopeOf(addons) {
    return closest(addons, '[data-section-id]') || document;
  }

  function remember(addons) {
    // Keep the theme's own price markup, so "no add-ons" shows it untouched.
    var scope = scopeOf(addons);
    var reg = scope.querySelector('[data-regular-price]');
    var cmp = scope.querySelector('[data-compare-price]');
    addons._sfRegular = reg ? reg.innerHTML : null;
    addons._sfCompare = cmp ? cmp.innerHTML : null;
  }

  function update(addons) {
    if (addons._sfRegular === undefined) remember(addons);
    var sample = addons.getAttribute('data-sf-money-sample') || '';
    var total = 0;
    selected(addons).forEach(function (p) { total += p.price; });

    each(addons.querySelectorAll('[data-sf-addon]'), function (field) {
      var sum = 0;
      each(field.querySelectorAll(INPUT), function (input) {
        if (input.checked && input.value) sum += Number(input.getAttribute('data-sf-price')) || 0;
      });
      var label = field.querySelector('[data-sf-addon-title-price]');
      if (label) label.textContent = sum ? '(+ ' + formatMoney(sum, sample) + ')' : '';
    });

    var box = addons.querySelector('[data-sf-addons-total]');
    var amount = addons.querySelector('[data-sf-addons-total-amount]');
    if (amount) amount.textContent = formatMoney(total, sample);
    if (box) box.hidden = !total;

    var scope = scopeOf(addons);
    var reg = scope.querySelector('[data-regular-price]');
    var cmp = scope.querySelector('[data-compare-price]');
    var base = Number(addons.getAttribute('data-sf-variant-price')) || 0;
    var baseCompare = Number(addons.getAttribute('data-sf-compare-price')) || 0;
    if (reg) {
      if (total) reg.textContent = formatMoney(base + total, sample);
      else if (addons._sfRegular !== null) reg.innerHTML = addons._sfRegular;
    }
    if (cmp && baseCompare > 0) {
      if (total) cmp.textContent = formatMoney(baseCompare + total, sample);
      else if (addons._sfCompare !== null) cmp.innerHTML = addons._sfCompare;
    }
    addons.setAttribute('data-sf-total', String(total));
  }
  api.update = update;

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!matches(input, INPUT)) return;
    var addons = closest(input, ROOT);
    if (addons) update(addons);
  });

  function initProductForms() {
    each(document.querySelectorAll(ROOT), function (addons) {
      remember(addons);
      // Back/forward cache and browser form restore can bring back old picks.
      update(addons);
    });
  }

  function onVariantChange(e, instance, product, variant) {
    var container = instance && instance.container;
    if (!container || !variant) return;
    each(container.querySelectorAll(ROOT), function (addons) {
      addons.setAttribute('data-sf-variant-id', variant.id);
      addons.setAttribute('data-sf-variant-price', variant.price);
      addons.setAttribute('data-sf-compare-price', variant.compare_at_price || 0);
      remember(addons); // the theme has just written the new variant's price
      update(addons);
    });
  }

  /* ----------------------------------------------- product: shipping notice */

  function noticeConfig() {
    var el = document.getElementById('sf-ship-notice-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // The product's "Ships in" value as entered (normally a number of weeks).
  function shipWeeks(form) {
    var el = form.querySelector('[data-sf-ship-weeks]');
    var raw = el ? String(el.getAttribute('data-sf-ship-weeks') || '').trim() : '';
    return raw && Number(raw) !== 0 ? raw : '';
  }

  function noticeText(cfg, raw) {
    var unit = Number(raw) === 1 ? cfg.week : cfg.weeks;
    return { weeks: raw + ' ' + unit, before: cfg.before || '', after: cfg.after || '' };
  }

  function confirmShipping(cfg, raw) {
    var dialog = document.getElementById('sf-ship-notice');
    var parts = noticeText(cfg, raw);
    if (!dialog || typeof dialog.showModal !== 'function') {
      return Promise.resolve(window.confirm(parts.before + parts.weeks + parts.after));
    }
    var weeksEl = dialog.querySelector('[data-sf-ship-notice-weeks]');
    if (weeksEl) weeksEl.textContent = parts.weeks;
    return new Promise(function (resolve) {
      var done = false;
      function finish(ok) {
        if (done) return;
        done = true;
        dialog.removeEventListener('close', onClose);
        dialog.removeEventListener('click', onClick);
        if (dialog.open) dialog.close();
        resolve(ok);
      }
      function onClose() { finish(dialog.returnValue === 'continue'); }
      function onClick(e) {
        var btn = closest(e.target, '[data-sf-ship-notice-action]');
        if (btn) {
          e.preventDefault();
          finish(btn.getAttribute('data-sf-ship-notice-action') === 'continue');
        } else if (e.target === dialog) {
          // The inner form fills the dialog, so a click on the <dialog> itself
          // is a click on the backdrop.
          finish(false);
        }
      }
      dialog.returnValue = '';
      dialog.addEventListener('close', onClose);
      dialog.addEventListener('click', onClick);
      dialog.showModal();
      var primary = dialog.querySelector('[data-sf-ship-notice-action="continue"]');
      if (primary) primary.focus();
    });
  }

  /* ------------------------------------------------- product: add to cart */

  function isProductForm(form) {
    return /\/cart\/add/.test(form.getAttribute('action') || '');
  }

  function submitButton(form) {
    return form.querySelector('[data-cart-submit]') || form.querySelector('[type="submit"]');
  }

  function setBusy(form, busy) {
    var btn = submitButton(form);
    form._sfBusy = busy;
    if (!btn) return;
    if (busy) {
      form._sfWasDisabled = btn.disabled;
      btn.disabled = true;
      btn.classList.add('sf-busy');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.disabled = !!form._sfWasDisabled;
      btn.classList.remove('sf-busy');
      btn.removeAttribute('aria-busy');
    }
  }

  function showError(form, message) {
    var note = form.querySelector('[data-cart-notification]');
    var text = message || cartErrorText();
    if (note) {
      note.textContent = text;
      note.classList.add('product__notification--error');
      note.classList.remove('product__notification--success');
      note.setAttribute('role', 'alert');
    } else {
      window.alert(text);
    }
  }

  function clearError(form) {
    var note = form.querySelector('[data-cart-notification]');
    if (note && note.classList.contains('product__notification--error')) {
      note.textContent = '';
      note.classList.remove('product__notification--error');
      note.removeAttribute('role');
    }
  }

  // Read the product form without FormData (older Safari lacks get/forEach).
  function formValues(form) {
    var out = { id: '', quantity: '', sellingPlan: '', properties: {} };
    each(form.elements, function (el) {
      var name = el.name;
      if (!name || el.disabled || matches(el, ':disabled')) return;
      var type = (el.type || '').toLowerCase();
      if (type === 'submit' || type === 'button' || type === 'reset' || type === 'image' || type === 'file') return;
      if ((type === 'radio' || type === 'checkbox') && !el.checked) return;
      var value = el.value;
      if (name === 'id') out.id = value;
      else if (name === 'quantity') out.quantity = value;
      else if (name === 'selling_plan') out.sellingPlan = value;
      else {
        var m = /^properties\[(.+)\]$/.exec(name);
        if (m && value !== '') out.properties[m[1]] = value;
      }
    });
    return out;
  }

  function mainItem(form, picks) {
    var v = formValues(form);
    var p = v.properties;
    var sig = picks.map(function (x) { return x.id; }).sort(function (a, b) { return a - b; }).join('+');
    if (sig) p._addons = sig; // hidden; keeps each set of choices on its own cart line
    var item = { id: Number(v.id), quantity: Math.max(1, parseInt(v.quantity, 10) || 1) };
    if (Object.keys(p).length) item.properties = p;
    if (v.sellingPlan) item.selling_plan = v.sellingPlan;
    return { item: item, sig: sig };
  }

  function propsKey(p) {
    p = p || {};
    return Object.keys(p).sort().map(function (k) {
      var v = p[k] === null || p[k] === undefined ? '' : String(p[k]);
      return k + '=' + v.trim();
    }).join('&');
  }

  // The cart line Shopify merges this add into: same variant, same properties.
  function sameLine(item, main) {
    return Number(item.variant_id) === main.item.id && !item.parent_relationship &&
      propsKey(props(item)) === propsKey(main.item.properties);
  }

  function quantityIn(cart, main) {
    var q = 0;
    ((cart && cart.items) || []).forEach(function (item) {
      if (sameLine(item, main)) q += item.quantity;
    });
    return q;
  }

  // Put the main product's line back to its quantity before this add.
  function restoreMain(main, previous) {
    function attempt() {
      return getCart().then(function (cart) {
        var line = null;
        ((cart && cart.items) || []).forEach(function (item) {
          if (!line && sameLine(item, main)) line = item;
        });
        if (!line || line.quantity <= previous) return null;
        return request('cart/change.js', { id: line.key, quantity: previous });
      });
    }
    return attempt().catch(attempt).catch(function () { return null; });
  }

  function addWithAddons(form, addons) {
    var picks = selected(addons);
    var main = mainItem(form, picks);
    if (!main.item.id) return Promise.reject(new Error('Please choose an option.'));
    var qty = main.item.quantity;
    var previous = 0;

    // The quantity already in the cart is needed to undo a failed add safely,
    // so nothing is added without it.
    return getCart()
      .then(function (cart) { previous = quantityIn(cart, main); }, function () { throw new Error(cartErrorText()); })
      .then(function () {
        return request('cart/add.js', { items: [main.item] }).catch(function (err) {
          // Shopify can add part of the quantity and still answer with an error.
          return restoreMain(main, previous).then(function () { throw err; });
        });
      })
      .then(function (res) {
        if (!picks.length) return null;
        var line = res && res.items && res.items[0];
        if (!line || !line.key) {
          return restoreMain(main, previous).then(function () {
            throw new Error('Could not add the add-ons. Please try again.');
          });
        }
        var owner = main.item.id + ':' + main.sig;
        var nested = picks.map(function (p) {
          return { id: p.id, quantity: qty, parent_line_key: line.key, properties: { _addon_for: owner } };
        });
        return request('cart/add.js', { items: nested })
          .catch(function (err) {
            // Nested lines refused (e.g. a store with the Script Editor): add
            // them as plain lines; the cart code still pairs them by _addon_for.
            if (err && err.status === 422 && /parent/i.test(err.message || '')) {
              return request('cart/add.js', {
                items: nested.map(function (n) { return { id: n.id, quantity: n.quantity, properties: n.properties }; })
              });
            }
            throw err;
          })
          .catch(function (err) {
            // Never leave the freeze dryer in the cart without the add-ons the
            // buyer chose (nested add-ons go with it if it is removed).
            return restoreMain(main, previous).then(function () { throw err; });
          });
      });
  }

  function resubmit(form, submitter) {
    form.setAttribute('data-sf-pass', '1');
    if (typeof form.requestSubmit === 'function') {
      if (submitter && submitter.form === form) form.requestSubmit(submitter);
      else form.requestSubmit();
      return;
    }
    if (form.dispatchEvent(newEvent('submit'))) {
      form.removeAttribute('data-sf-pass');
      if (submitter && submitter.name) {
        // A plain submit() leaves out the clicked button (e.g. "checkout").
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = submitter.name;
        hidden.value = submitter.value || '';
        form.appendChild(hidden);
      }
      HTMLFormElement.prototype.submit.call(form);
    }
  }

  function onProductSubmit(e, form) {
    var addons = form.querySelector(ROOT);
    var cfg = noticeConfig();
    var weeks = cfg && cfg.enabled ? shipWeeks(form) : '';
    if (!addons && !weeks) return;
    if (addons && !supportsFetch) return; // very old browser: plain form post

    stop(e);
    if (form._sfBusy) return;

    var submitter = e.submitter || null;
    clearError(form);
    var ask = weeks ? confirmShipping(cfg, weeks) : Promise.resolve(true);
    form._sfBusy = true;
    ask.then(function (ok) {
      form._sfBusy = false;
      if (!ok) return;
      if (!addons) {
        resubmit(form, submitter);
        return;
      }
      setBusy(form, true);
      Promise.resolve()
        .then(function () { return addWithAddons(form, addons); })
        .then(
          function () { window.location.href = cartUrl(); },
          function (err) {
            setBusy(form, false);
            showError(form, err && err.message);
          }
        );
    });
  }

  /* ------------------------------------------------------------------ cart */

  var data = (function () {
    var el = document.getElementById('sf-addons-data');
    var parsed = null;
    try { parsed = el ? JSON.parse(el.textContent) : null; } catch (e) { parsed = null; }
    parsed = parsed || {};
    var ids = {};
    (parsed.addons || []).forEach(function (id) { ids[String(id)] = true; });
    return {
      addonIds: ids,
      check: !!parsed.check,
      removed: parsed.removed || 'was removed from your cart: add-ons are only sold with the freeze dryer they belong to.',
      mismatch: parsed.mismatch || 'could not be set to the same quantity as the freeze dryer (not enough in stock). Change the quantity, or press Check out again to continue anyway.'
    };
  })();
  api.data = data;

  var MAX_RUNS = 10;          // fixes per RUN_WINDOW ms at most (a guard against loops)
  var RUN_WINDOW = 10000;
  var REFUSED_FOR = 20000;    // a fix Shopify refused is not asked for again for 20 s
  var TYPING_PAUSE = 700;     // the theme sends a typed quantity 500 ms after the last keystroke

  var state = {
    last: null,               // newest cart known
    drawn: null,              // the cart the page shows
    running: null,            // promise of the fix in progress
    removing: false,          // a fix that takes lines out (renumbers lines) is not drawn yet
    held: [],                 // Remove clicks / typing held meanwhile: [{ id, fn }]
    heldKeys: {},
    replaying: false,
    animEl: null,             // the theme's Remove animation being watched, since when
    animSince: 0,
    refused: {},              // fix signature -> time Shopify refused it
    failures: {},             // fix signature -> failed requests in a row
    stamps: [],               // start times of recent fixes
    timer: null,
    timerAt: 0,
    deferSince: 0,
    epoch: 0,                 // count of the theme's own cart redraws
    themeRequests: 0,         // the theme's cart requests on their way
    lastInput: 0,             // last time the buyer typed a quantity
    hasAddons: false,
    bypass: null,             // mismatch the buyer was told about at Check out
    selfUpdate: false,
    retry: null,
    retries: 0
  };
  api.state = state;

  // For each line: is it one of our add-ons, and which line does it belong to?
  function links(cart) {
    var items = (cart && cart.items) || [];
    var byKey = {};
    var bySig = {};
    var byGroup = {};
    items.forEach(function (item, i) {
      byKey[item.key] = i;
      var p = props(item);
      if (p._addons) bySig[item.variant_id + ':' + p._addons] = i;
      if (p._gpo_product_group) byGroup[String(p._gpo_product_group)] = i;
    });
    return items.map(function (item) {
      var p = props(item);
      var rel = item.parent_relationship;
      var ours = !!p._addon_for || !!data.addonIds[String(item.variant_id)];
      if (rel && rel.parent_key) {
        // Nested lines from other apps (a warranty, say) are left alone.
        if (!ours) return { addon: false, parent: -1 };
        return { addon: true, parent: byKey.hasOwnProperty(rel.parent_key) ? byKey[rel.parent_key] : -1 };
      }
      if (p._addon_for) {
        return { addon: true, parent: bySig.hasOwnProperty(p._addon_for) ? bySig[p._addon_for] : -1 };
      }
      if (p._gpo_parent_product_group) {
        var g = String(p._gpo_parent_product_group);
        return { addon: true, parent: byGroup.hasOwnProperty(g) ? byGroup[g] : -1 };
      }
      if (ours) return { addon: true, parent: -1 };
      return { addon: false, parent: -1 };
    });
  }
  api.links = links;

  // Add-ons follow their product's quantity (one per freeze dryer, as with
  // Globo); add-ons without their product go. Keyed by line key.
  function plan(cart) {
    var items = (cart && cart.items) || [];
    var updates = {};
    var parts = [];
    var titles = [];
    var removes = false;
    links(cart).forEach(function (l, i) {
      if (!l.addon) return;
      var want = l.parent >= 0 ? items[l.parent].quantity : 0;
      if (items[i].quantity !== want) {
        updates[items[i].key] = want;
        parts.push(items[i].key + '=' + want);
        if (titles.indexOf(items[i].title) === -1) titles.push(items[i].title);
        if (want === 0) removes = true;
      }
    });
    parts.sort();
    return { updates: updates, count: parts.length, sig: parts.join('|'), titles: titles, removes: removes };
  }
  api.plan = plan;

  function hasAddonLines(cart) {
    return links(cart).some(function (l) { return l.addon; });
  }

  function refusedRecently(sig) {
    var t = state.refused[sig];
    return !!t && Date.now() - t < REFUSED_FOR;
  }

  // How long until another fix may start (0: now).
  function slotWait() {
    var now = Date.now();
    state.stamps = state.stamps.filter(function (t) { return now - t < RUN_WINDOW; });
    return state.stamps.length < MAX_RUNS ? 0 : RUN_WINDOW - (now - state.stamps[0]) + 50;
  }

  // A cart row the theme is animating out before it sends the removal. Its
  // animations take under a second, so a class left on longer is ignored.
  var REMOVING = '[data-cart-item].ajax-cart__item--remove:not([data-cart-note-container]):not(.ajax-cart__template)';

  function removalAnimating() {
    var el = document.querySelector(REMOVING);
    if (!el) {
      state.animEl = null;
      return false;
    }
    if (el !== state.animEl) {
      state.animEl = el;
      state.animSince = Date.now();
    }
    return Date.now() - state.animSince < 3000;
  }

  // The theme may be about to act on a line number: a Remove animating, a cart
  // request of its own on its way, or a typed quantity it has not sent yet.
  function themeActive() {
    return removalAnimating() || state.themeRequests > 0 || Date.now() - state.lastInput < TYPING_PAUSE;
  }

  function recheckLater(ms) {
    var at = Date.now() + ms;
    if (state.timer && state.timerAt <= at) return;
    clearTimeout(state.timer);
    state.timerAt = at;
    state.timer = setTimeout(function () {
      state.timer = null;
      if (state.last) reconcile(state.last);
    }, ms);
  }

  function removedTitles(updates, before, after) {
    var still = {};
    ((after && after.items) || []).forEach(function (item) { still[item.key] = true; });
    var out = [];
    (before || []).forEach(function (item) {
      if (updates[item.key] === 0 && !still[item.key] && out.indexOf(item.title) === -1) out.push(item.title);
    });
    return out;
  }

  function reconcile(cart) {
    if (!cart || !cart.items) return;
    state.last = cart;
    if (hasAddonLines(cart)) state.hasAddons = true;
    if (state.running || state.removing) return; // the fix in progress re-reads the cart when it ends
    var p = plan(cart);
    if (!p.count || refusedRecently(p.sig) || !supportsFetch) {
      decorate(cart);
      return;
    }
    if (p.removes && themeActive()) {
      // Taking lines out renumbers the rest: not while the theme may act on a
      // line number (checked again shortly; goes ahead after 6 s regardless).
      if (!state.deferSince) state.deferSince = Date.now();
      if (Date.now() - state.deferSince < 6000) {
        decorate(cart);
        recheckLater(300);
        return;
      }
    }
    var wait = slotWait();
    if (wait > 0) {
      decorate(cart);
      recheckLater(wait);
      return;
    }
    state.deferSince = 0;
    run(p, cart);
  }
  api.reconcile = reconcile;

  function run(p, cart) {
    var before = cart.items;
    var answer = null;
    var backoff = 0;
    state.stamps.push(Date.now());
    state.removing = p.removes;
    // Show the add-on quantities about to be set.
    decorate(cart);
    each(document.querySelectorAll(CARTS), mirror);
    state.running = request('cart/update.js', { updates: p.updates })
      .then(function (after) {
        answer = after;
        delete state.failures[p.sig];
        // Shopify answered but kept the old quantities: don't ask again for a while.
        var again = plan(after);
        if (again.count && again.sig === p.sig) state.refused[p.sig] = Date.now();
      }, function (err) {
        var status = err && err.status;
        if (status >= 400 && status < 500 && status !== 429) {
          state.refused[p.sig] = Date.now(); // refused, e.g. not enough in stock
        } else {
          // Network trouble or a server error: try again shortly (3 tries).
          var n = (state.failures[p.sig] || 0) + 1;
          state.failures[p.sig] = n;
          if (n >= 3) state.refused[p.sig] = Date.now();
          else backoff = 1000 * n;
        }
      })
      .then(function () { return readLatest(0); })
      .then(function (latest) {
        latest = latest || answer;
        var drawn = false;
        if (latest && latest.items) {
          state.last = latest;
          drawn = refresh(latest, removedTitles(p.updates, before, latest));
        }
        state.running = null;
        if (p.removes && !drawn) {
          // Lines may have been taken out but the page can't show it yet (no
          // answer from Shopify): keep holding Remove and typing until it does.
          awaitRedraw(1, backoff);
          return;
        }
        state.removing = false;
        replayHeld();
        if (backoff) recheckLater(backoff);
        else if (latest && latest.items) reconcile(latest);
      })
      .catch(function () {
        state.running = null;
        if (p.removes) awaitRedraw(1, 0);
        else replayHeld();
      });
  }

  // After a removal the page must show the renumbered lines before held Remove
  // clicks and typing go to the theme. Re-reads the cart until it can redraw;
  // after ~20 s without an answer, drops what was held (replaying it on the
  // old line numbers could change the wrong line).
  function awaitRedraw(n, backoff) {
    state.removing = true;
    setTimeout(function () {
      readLatest(0).then(function (cart) {
        if (cart && cart.items) {
          state.last = cart;
          if (refresh(cart, null)) {
            state.removing = false;
            replayHeld();
            if (backoff) recheckLater(backoff);
            else reconcile(cart);
            return;
          }
        }
        if (n < 7) {
          awaitRedraw(n + 1, backoff);
          return;
        }
        state.held = [];
        state.heldKeys = {};
        state.removing = false;
        themeRefresh(); // the theme redraws once the connection is back
      });
    }, Math.min(500 * n, 4000));
  }

  function themeRefresh() {
    var themeApi = window.theme && theme.sections;
    if (!themeApi || typeof themeApi.getInstances !== 'function') return;
    ['cart-template', 'cart-drawer'].forEach(function (type) {
      var d = themeApi.getInstances(type);
      var on = d && (typeof d.done === 'function' ? d.done : d.then);
      if (!on) return;
      on.call(d, function (list) {
        (list || []).forEach(function (inst) {
          if (inst && typeof inst.refresh === 'function') {
            try { inst.refresh(); } catch (e) { /* ignore */ }
          }
        });
      });
    });
  }

  // The cart as it is now. If the theme redrew the cart while this read was on
  // its way, Shopify may have answered it before the theme's change: read again.
  function readLatest(tries) {
    var epoch = state.epoch;
    return getCart().then(function (cart) {
      if (state.epoch !== epoch && tries < 3) return readLatest(tries + 1);
      return cart;
    }, function () { return null; });
  }

  // Hand held Remove clicks and typing to the theme one at a time, each once
  // the theme has finished the previous one (it acts on line numbers).
  function replayHeld() {
    if (state.replaying) return;
    state.replaying = true;
    var since = Date.now();
    (function next() {
      if (!state.held.length) {
        state.replaying = false;
        return;
      }
      // Never while lines are being renumbered; for the theme's own request,
      // up to 10 s (a request that never ends must not swallow the rest).
      if (state.running || state.removing || (themeActive() && Date.now() - since < 10000)) {
        setTimeout(next, 100);
        return;
      }
      var entry = state.held.shift();
      delete state.heldKeys[entry.id];
      try { entry.fn(); } catch (e) { /* the rest still runs */ }
      since = Date.now();
      setTimeout(next, 100);
    })();
  }

  function hold(id, fn) {
    if (state.heldKeys[id]) return;
    state.heldKeys[id] = true;
    state.held.push({ id: id, fn: fn });
  }

  // Redraw the cart page / drawer through the theme. Returns false when it had
  // to wait (the theme is animating a Remove: redrawing would cancel it).
  function refresh(cart, removed) {
    clearNotes();
    if (removalAnimating()) {
      // The theme's own redraw follows; this one is tried again in case that
      // removal fails.
      if (!state.retry && state.retries < 5) {
        state.retries++;
        state.retry = setTimeout(function () {
          state.retry = null;
          if (!state.running && state.last) refresh(state.last, removed);
        }, 1500);
      }
      return false;
    }
    state.retries = 0;
    var typed = pendingInputs();

    var themeApi = window.theme && theme.sections;
    if (themeApi && typeof themeApi.getInstances === 'function') {
      ['cart-template', 'cart-drawer'].forEach(function (type) {
        var d = themeApi.getInstances(type);
        // jQuery Deferred: .done runs at once when already resolved.
        var on = d && (typeof d.done === 'function' ? d.done : d.then);
        if (!on) return;
        on.call(d, function (list) {
          (list || []).forEach(function (inst) {
            if (!inst || typeof inst.update !== 'function') return;
            state.selfUpdate = true;
            try { inst.update(cart); } catch (e) { /* the rest still runs */ } finally { state.selfUpdate = false; }
          });
        });
      });
    }
    state.drawn = cart;
    var empty = !cart.items.length;
    each(document.querySelectorAll('[data-section-type="cart-template"]'), function (el) {
      el.classList.toggle('cart--empty', empty);
    });
    each(document.querySelectorAll('.cart-drawer'), function (el) {
      el.classList.toggle('cart-drawer--empty', empty);
    });
    each(document.querySelectorAll('.site-header__cart-bubble'), function (el) {
      el.classList.toggle('site-header__cart-bubble--visible', !!cart.item_count);
    });
    // Other open tabs redraw their cart from this (the theme's own sync).
    try { window.localStorage.setItem('shopify_cart_state', JSON.stringify(cart)); } catch (e) { /* private mode */ }

    restoreInputs(typed);
    decorate(cart);
    each(document.querySelectorAll(CARTS), mirror);
    if (removed && removed.length) showMessage(removed.join(', ') + ' ' + data.removed, empty);
    return true;
  }

  function findRow(container, key) {
    var rows = rowsOf(container);
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].getAttribute('data-cart-item-id') === key) return rows[i];
    }
    return null;
  }

  // Quantities the buyer typed that the page does not show from the cart yet
  // (the theme sends them after a pause, or its answer is on its way): kept
  // through a redraw, so the theme still sends what the buyer typed.
  function pendingInputs() {
    var drawn = {};
    if (state.drawn) state.drawn.items.forEach(function (item) { drawn[item.key] = String(item.quantity); });
    var parents = parentKeys();
    var out = [];
    each(document.querySelectorAll(CARTS), function (container) {
      rowsOf(container).forEach(function (row) {
        var key = row.getAttribute('data-cart-item-id');
        var input = row.querySelector(QTY);
        if (!key || !input || parents[key] || input.value === '') return;
        var shown = state.drawn ? drawn[key] : input.defaultValue;
        if (shown !== undefined && input.value !== shown) out.push({ container: container, key: key, value: input.value });
      });
    });
    return out;
  }

  function restoreInputs(list) {
    list.forEach(function (t) {
      var row = findRow(t.container, t.key);
      var input = row && row.querySelector(QTY);
      if (input && input.value !== t.value) input.value = t.value;
    });
  }

  function clearNotes() {
    each(document.querySelectorAll('.sf-cart-note'), function (el) {
      el.parentNode.removeChild(el);
    });
  }

  function showMessage(msg, empty) {
    each(document.querySelectorAll(CARTS), function (container) {
      var el = container.querySelector('[data-cart-message]');
      if (el) {
        el.textContent = msg;
        // The theme only shows this message area in its error state.
        container.classList.add('ajax-cart--error');
      }
      if (!empty) return;
      // An empty cart hides that area: say it in the "cart is empty" block.
      var host = container.querySelector('.cart__empty .cart-message, .cart-drawer__empty-text');
      if (host) {
        var note = document.createElement('p');
        note.className = 'sf-cart-note';
        note.setAttribute('role', 'status');
        note.textContent = msg;
        host.insertBefore(note, host.firstChild);
      }
    });
  }

  function rowsOf(container) {
    var rows = [];
    each(container.querySelectorAll('[data-cart-item]'), function (row) {
      if (row.classList.contains('ajax-cart__template') || row.hasAttribute('data-cart-note-container')) return;
      rows.push(row);
    });
    return rows;
  }

  // Add-on rows: quantity shown but not editable; Remove hidden (CSS).
  function decorate(cart) {
    var items = (cart && cart.items) || [];
    var map = links(cart);
    each(document.querySelectorAll(CARTS), function (container) {
      rowsOf(container).forEach(function (row) {
        var i = Number(row.getAttribute('data-cart-item-line-number')) - 1;
        var item = items[i];
        // Only lines that belong to a freeze dryer still in the cart; a leftover
        // (being removed) keeps its Remove link in case removal fails.
        var addon = !!(item && map[i] && map[i].addon && map[i].parent >= 0 && row.getAttribute('data-cart-item-id') === item.key);
        row.classList.toggle('sf-cart-addon', addon);
        each(row.querySelectorAll(QTY), function (input) {
          // Undo only what was set here (the theme sets tabindex on the boxes
          // of a closed drawer itself).
          if (addon && !input.hasAttribute('data-sf-locked')) {
            input.setAttribute('data-sf-locked', input.getAttribute('tabindex') || '');
            input.setAttribute('tabindex', '-1');
            input.readOnly = true;
          } else if (!addon && input.hasAttribute('data-sf-locked')) {
            var before = input.getAttribute('data-sf-locked');
            if (before) input.setAttribute('tabindex', before);
            else input.removeAttribute('tabindex');
            input.removeAttribute('data-sf-locked');
            input.readOnly = false;
          }
        });
      });
    });
  }
  api.decorate = decorate;

  // Add-on line key -> its freeze dryer's line key, from the newest cart known.
  function parentKeys() {
    var items = (state.last && state.last.items) || [];
    var out = {};
    links(state.last).forEach(function (l, i) {
      if (l.addon && l.parent >= 0) out[items[i].key] = items[l.parent].key;
    });
    return out;
  }

  // Copy each freeze dryer's quantity box into its add-on rows, so they show
  // the same number and the cart page's Check out posts matching quantities.
  function mirror(container) {
    if (!container || !state.last) return;
    var parents = parentKeys();
    var rows = rowsOf(container);
    var values = {};
    rows.forEach(function (row) {
      var key = row.getAttribute('data-cart-item-id');
      var input = row.querySelector(QTY);
      if (input && key && !parents[key]) values[key] = input.value;
    });
    rows.forEach(function (row) {
      var parentKey = parents[row.getAttribute('data-cart-item-id')];
      if (!parentKey || values[parentKey] === undefined) return;
      var v = parseInt(values[parentKey], 10);
      if (isNaN(v) || v < 0) return;
      each(row.querySelectorAll(QTY), function (input) {
        if (input.value !== String(v)) input.value = String(v);
      });
    });
  }
  api.mirror = mirror;

  // Typing a quantity. While lines are being taken out (renumbered), the theme
  // hears about it only afterwards: it sends quantities by line number.
  document.addEventListener('input', function (e) {
    var input = e.target;
    if (!matches(input, QTY) || input.readOnly) return;
    var container = closest(input, CARTS);
    if (!container) return;
    state.lastInput = Date.now();
    if (state.removing) {
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      else e.stopPropagation();
      var row = closest(input, '[data-cart-item]');
      var key = row && row.getAttribute('data-cart-item-id');
      if (key) {
        hold('q' + key, function () {
          var r = findRow(container, key);
          var box = r && r.querySelector(QTY);
          if (!box) return;
          if (box.value !== input.value) box.value = input.value;
          box.dispatchEvent(newEvent('input'));
        });
      }
    }
    mirror(container);
  }, true);

  // Remove clicked while lines are being taken out: done right after, on the
  // renumbered line (the theme removes by the line number it reads on click).
  document.addEventListener('click', function (e) {
    if (!state.removing) return;
    var link = closest(e.target, '[data-cart-item-delete]');
    var container = link && closest(link, CARTS);
    if (!container) return;
    stop(e);
    var row = closest(link, '[data-cart-item]');
    var key = row && row.getAttribute('data-cart-item-id');
    if (!key) return;
    hold('x' + key, function () {
      var r = findRow(container, key);
      var again = r && r.querySelector('[data-cart-item-delete]');
      if (again) again.click();
    });
  }, true);

  function isCartForm(form) {
    var action = (form.getAttribute('action') || '').split('?')[0];
    return /\/cart\/?$/.test(action) && !!closest(form, CARTS);
  }

  function cartBusy() {
    return !!state.running || state.removing || state.replaying || state.held.length > 0 || themeActive();
  }

  // Resolves once nothing has been changing the cart for a moment (the theme
  // finishes a change a few ms after its request ends), or at the deadline.
  function settle(deadline) {
    return new Promise(function (resolve) {
      var quietSince = cartBusy() ? 0 : Date.now() - 150; // already quiet: go on
      (function poll() {
        var now = Date.now();
        if (cartBusy()) quietSince = 0;
        else if (!quietSince) quietSince = now;
        if ((quietSince && now - quietSince >= 150) || now >= deadline) return resolve();
        setTimeout(poll, 50);
      })();
    });
  }

  // Before checkout: the cart as Shopify has it, with every add-on matching its
  // freeze dryer (fixed here if needed). null: the cart could not be read.
  function verifyCart() {
    return getCart().then(function (cart) {
      var p = plan(cart);
      if (!p.count) return { ok: true, cart: cart };
      return request('cart/update.js', { updates: p.updates })
        .then(null, function () { return null; })
        .then(function () { return getCart(); })
        .then(function (after) {
          var again = plan(after);
          return { ok: !again.count, cart: after, sig: again.sig, titles: again.titles };
        });
    }).then(null, function () { return null; });
  }

  // Does the page show exactly this cart (same lines, order and quantities)?
  function inSync(container, cart) {
    var rows = rowsOf(container);
    if (rows.length !== cart.items.length) return false;
    for (var i = 0; i < rows.length; i++) {
      var input = rows[i].querySelector(QTY);
      if (rows[i].getAttribute('data-cart-item-id') !== cart.items[i].key) return false;
      if (rows[i].getAttribute('data-cart-item-line-number') !== String(i + 1)) return false;
      if (input && input.value !== String(cart.items[i].quantity)) return false;
    }
    return true;
  }

  function onCartSubmit(e, form) {
    var container = closest(form, CARTS);
    if (!supportsFetch || !(data.check || state.hasAddons)) {
      mirror(container);
      return; // no add-ons in this cart: nothing to check
    }
    stop(e);
    if (form._sfGate) return;
    form._sfGate = true;
    var submitter = e.submitter || form.querySelector('[type="submit"][name="checkout"]');
    if (submitter) submitter.classList.add('btn--loader-active');
    // At most ~12 s in all; after that (a stalled connection) checkout goes
    // ahead as it would without this check.
    var checked = settle(Date.now() + 6000).then(verifyCart);
    Promise.race([checked, delay(12000).then(function () { return null; })])
      .then(null, function () { return null; })
      .then(function (res) {
        form._sfGate = false;
        if (submitter) submitter.classList.remove('btn--loader-active');
        var c = closest(form, CARTS) || container;
        if (res && res.cart) {
          state.last = res.cart;
          // The cart page posts every line's quantity by position: make the
          // page show exactly the cart Shopify has before it does. (If it
          // can't redraw right now - a Remove just started - press again.)
          if (c && !inSync(c, res.cart) && !refresh(res.cart, null) && form.querySelector('[name="updates[]"]')) return;
        }
        if (res && !res.ok && state.bypass !== res.sig) {
          // Shopify would not match them (e.g. not enough in stock): say so;
          // pressing Check out again goes on to checkout.
          state.bypass = res.sig;
          showMessage(res.titles.join(', ') + ' ' + data.mismatch, false);
          return;
        }
        mirror(c);
        resubmit(form, submitter); // the theme puts its own loader back on
      });
  }

  // Capture phase on document: runs before the theme's own (jQuery, bubbling)
  // handlers for the product form's Add to cart and the cart's Check out.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.hasAttribute('data-sf-pass')) {
      form.removeAttribute('data-sf-pass');
      return; // our own resubmit: the theme handles it as usual
    }
    if (e.defaultPrevented) return;
    if (isProductForm(form)) onProductSubmit(e, form);
    else if (isCartForm(form)) onCartSubmit(e, form);
  }, true);

  // Back button after leaving the page: make the buttons usable again.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    each(document.querySelectorAll('form'), function (form) {
      if (form._sfBusy) setBusy(form, false);
      form._sfGate = false;
    });
  });

  function isCartRequest(settings) {
    return /\/cart(\.js|\/|\?|$)/.test((settings && settings.url) || '');
  }

  function bindTheme() {
    var $ = window.jQuery;
    if (!$) return;
    $(document).on('cart_update_done.sfAddons', function (e, instance, cart) {
      if (state.selfUpdate || !cart || !cart.items) return; // our own redraw
      state.epoch++;
      state.drawn = cart;
      clearNotes();
      reconcile(cart);
    });
    // The theme's cart requests (jQuery): line numbers may be in use meanwhile.
    $(document).on('ajaxSend.sfAddons', function (e, xhr, settings) {
      if (isCartRequest(settings)) state.themeRequests++;
    });
    $(document).on('ajaxComplete.sfAddons', function (e, xhr, settings) {
      if (isCartRequest(settings)) state.themeRequests = Math.max(0, state.themeRequests - 1);
    });
    $(document).on('variant_change.sfAddons', onVariantChange);
  }

  function init() {
    initProductForms();
    bindTheme();
    if (data.check && supportsFetch) {
      getCart().then(reconcile, function () {});
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
